import type { ShopperProducts } from '@/scapi';
import { buildBrandCategoryLookup, type BrandsCuratedVariant } from '@/extensions/snk/lib/brands-utils';
import { isCategoryShownInMenu } from '@/extensions/snk/lib/utils';

type MegaMenuBrandsAttributeKey = 'c_spaceTopBrands' | 'c_bestSellingBrands' | 'c_defaultTopBrands';

const MEGA_MENU_CURATED_ATTRIBUTE_BY_VARIANT: Record<BrandsCuratedVariant, MegaMenuBrandsAttributeKey> = {
    affordable: 'c_spaceTopBrands',
    bestselling: 'c_bestSellingBrands',
    new: 'c_defaultTopBrands',
};

function resolveBrandAttributeEntry(
    lookup: Map<string, ShopperProducts.schemas['Category']>,
    entry: unknown
): ShopperProducts.schemas['Category'] | undefined {
    if (typeof entry === 'string' && entry.length > 0) {
        return lookup.get(entry.trim().toLowerCase());
    }

    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;

        if (typeof record.id === 'string' && record.id.length > 0) {
            const resolvedById = lookup.get(record.id.trim().toLowerCase());
            if (resolvedById) {
                return resolvedById;
            }
            return entry as ShopperProducts.schemas['Category'];
        }

        if (typeof record.name === 'string' && record.name.length > 0) {
            return lookup.get(record.name.trim().toLowerCase());
        }
    }

    return undefined;
}

function getCuratedBrandEntries(
    category: ShopperProducts.schemas['Category'],
    attributeKey: MegaMenuBrandsAttributeKey
): unknown[] {
    const value = category[attributeKey as keyof ShopperProducts.schemas['Category']];

    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string' && value.length > 0) {
        return [value];
    }

    return [];
}

export function getMegaMenuCuratedBrandCategories(
    brandsCategory: ShopperProducts.schemas['Category'],
    variant: BrandsCuratedVariant
): ShopperProducts.schemas['Category'][] {
    const attributeKey = MEGA_MENU_CURATED_ATTRIBUTE_BY_VARIANT[variant];
    const entries = getCuratedBrandEntries(brandsCategory, attributeKey);
    const lookup = buildBrandCategoryLookup(brandsCategory);

    return entries
        .map((entry) => resolveBrandAttributeEntry(lookup, entry))
        .filter((category): category is ShopperProducts.schemas['Category'] => Boolean(category))
        .filter(isCategoryShownInMenu);
}
